import OneSignal from "react-onesignal";
import { useCallback, useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { supabase } from "@/integrations/supabase/client";
import { Sentry } from "@/lib/sentry";
import {
  registerUserForPush,
  requestNotificationPermission,
  optInPushSubscription,
  onPushSubscriptionChange,
  subscriptionCapturePatch,
  type PushSubscriptionSnapshot,
} from "@/lib/oneSignal";

// Web push opt-in hook + native push opt-in (Capacitor).
// Web (Track A): react-onesignal Web SDK v16 — unchanged behavior.
// Native (Track B): onesignal-cordova-plugin v5 via @/lib/oneSignal which
// dynamic-imports the plugin only when Capacitor.isNativePlatform() is true.

export interface PushPermissionOwnerGuard { ownerId: string; isCurrent: () => boolean }

type PermissionOutcome = "granted" | "denied" | "unsupported";

interface UseOneSignalReturn {
  isSupported: boolean;
  isSubscribed: boolean;
  subscriptionId: string | null;
  requestPermission: (guard?: PushPermissionOwnerGuard) => Promise<PermissionOutcome>;
  saveOptInToDb: (granted: boolean, guard?: PushPermissionOwnerGuard) => Promise<void>;
}

function isNative(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

// Module-level guard: the push-subscription change listener must be attached
// exactly once per page lifetime no matter how many components mount the hook
// (Settings + Sleep + DailyCheckIn all use it).
let changeListenerAttached = false;
// Latest authenticated user id, readable from the module-level listener
// callback (the listener outlives any single hook instance).
let listenerUserId: string | null = null;

// A valid snapshot that arrived BEFORE we knew the user id (native mints the id
// at init, before login). Stashed here and flushed by flushPendingSnapshot once
// listenerUserId is set — otherwise that id was lost forever (a cause of the low
// captured-subid count).
let pendingSnapshot: PushSubscriptionSnapshot | null = null;

// Persist the subscription snapshot for diagnostics. This is the RELIABLE
// capture point for onesignal_subscription_id: the id is minted async after
// permission, so the old immediate read wrote NULL for 100% of users.
async function persistSubscriptionSnapshot(snap: PushSubscriptionSnapshot): Promise<void> {
  // Persist ONLY a real, opted-in subscription. Intermediate change events
  // (id still null, or opted out mid-flow) must not blank a previously
  // captured id with null.
  if (!snap.id || !snap.optedIn) return;
  const uid = listenerUserId;
  if (!uid) {
    // Pre-auth event — keep it and flush after login instead of dropping it.
    pendingSnapshot = snap;
    return;
  }
  pendingSnapshot = null;
  // Write de captura canônico (id + captured_at + dead_at=NULL) — ciclo de
  // vida completo documentado em subscriptionCapturePatch (lib/oneSignal).
  const { error } = await supabase
    .from("user_profiles")
    .update(subscriptionCapturePatch(snap.id) as never)
    .eq("user_id", uid);
  if (error) {
    console.warn("[useOneSignal] subscription snapshot save failed:", error);
    try {
      Sentry.captureException(error, {
        tags: { feature: "push_subscription_save_failed" },
      });
    } catch {
      // Sentry not initialized in dev.
    }
  }
}

// Flush a snapshot captured before the user id was known. Called once
// listenerUserId becomes available (post-login).
function flushPendingSnapshot(): void {
  if (pendingSnapshot && listenerUserId) {
    const snap = pendingSnapshot;
    pendingSnapshot = null;
    void persistSubscriptionSnapshot(snap);
  }
}

function detectIsSupported(): boolean {
  // On native (Capacitor iOS / Android) push is always supported by the
  // OneSignal Cordova plugin once it's bundled. No DOM checks.
  if (isNative()) return true;

  if (typeof window === "undefined") return false;
  if (!("Notification" in window) || !("serviceWorker" in navigator)) return false;
  // iOS Safari only supports web push if installed as a PWA on iOS 16.4+.
  // Outside the standalone display mode, the system-level subscription
  // never lands and the user just gets nothing — better to hide the prompt.
  const isStandalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true;
  const ua = navigator.userAgent;
  const isIOSSafari =
    /iPad|iPhone|iPod/.test(ua) &&
    /Safari/.test(ua) &&
    !/CriOS|FxiOS|EdgiOS/.test(ua);
  if (isIOSSafari && !isStandalone) return false;
  return true;
}

export function useOneSignal(userId: string | null): UseOneSignalReturn {
  const [isSupported, setIsSupported] = useState<boolean>(true);
  const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);

  // Keep the module-level listener pointing at the current user.
  const userIdRef = useRef<string | null>(userId);
  userIdRef.current = userId;
  useEffect(() => {
    if (userId) {
      listenerUserId = userId;
      // A subscription id minted before login (native init) was stashed —
      // persist it now that we know who this is.
      flushPendingSnapshot();
    }
  }, [userId]);

  useEffect(() => {
    const supported = detectIsSupported();
    setIsSupported(supported);
    if (!supported) return;

    // Both tracks: attach the change listener once per page lifetime. The
    // subscription id arrives via this observer (async after permission) —
    // it is the only reliable writer of onesignal_subscription_id.
    if (!changeListenerAttached) {
      changeListenerAttached = true;
      void onPushSubscriptionChange((snap) => {
        setSubscriptionId(snap.id);
        setIsSubscribed(snap.optedIn);
        void persistSubscriptionSnapshot(snap);
      });
    }

    if (isNative()) {
      // Native plugin exposes subscription state via observers (attached
      // above), not a synchronous getter we can read at mount. The opt-in
      // flow updates local state below via requestPermission.
      return;
    }

    // Web SDK: read current subscription state at mount.
    try {
      const sub = OneSignal.User?.PushSubscription;
      const id = sub?.id ?? null;
      const optedIn = sub?.optedIn === true;
      if (id) setSubscriptionId(id);
      if (optedIn) setIsSubscribed(true);
    } catch (err) {
      Sentry.captureException(err, {
        tags: { feature: "push_optin_detect_failed" },
      });
    }
  }, []);

  const saveOptInToDb = useCallback(
    async (granted: boolean, guard?: PushPermissionOwnerGuard): Promise<void> => {
      if (!userId) return;
      const assert = () => { if (guard && (!guard.isCurrent() || guard.ownerId !== userId || userIdRef.current !== userId)) throw new Error("invalid_session"); };
      assert();
      let token: string | null = null;
      if (guard) {
        const user = await supabase.auth.getUser(); assert();
        if (user.error || user.data.user?.id !== guard.ownerId) throw new Error("invalid_session");
        const session = await supabase.auth.getSession(); assert();
        if (session.error || session.data.session?.user.id !== guard.ownerId || !session.data.session.access_token) throw new Error("invalid_session");
        token = session.data.session.access_token;
      }
      // Immediate best-effort read; usually still null right after the
      // prompt (the id is minted async). The pushSubscription "change"
      // listener attached at mount is the RELIABLE writer of
      // onesignal_subscription_id — this write just seeds opt_in fields.
      // Delivery never depends on this column (edge fns target
      // include_aliases.external_id).
      const subId = !isNative() && granted
        ? OneSignal.User?.PushSubscription?.id ?? null
        : null;
      // Bug fix (null-stomp): only write the subscription id when we actually
      // have one. The old unconditional write set it to null on native (subId is
      // always null there) and on web before the id was minted, clobbering the
      // real id captured by persistSubscriptionSnapshot's change listener.
      // Mirrors that writer's guard (:54). Matters now that dispatchers prefer
      // include_subscription_ids targeting.
      const update: Record<string, unknown> = {
        push_opt_in: granted,
        push_opt_in_at: new Date().toISOString(),
      };
      // Captura via patch canônico (id + captured_at + dead_at=NULL) — mesma
      // regra dos outros dois writers; ver subscriptionCapturePatch.
      if (subId) Object.assign(update, subscriptionCapturePatch(subId));
      const request = supabase.from("user_profiles").update(update as never).eq("user_id", userId);
      if (token) request.setHeader("Authorization", "Bearer " + token);
      assert(); const { error } = await request; assert();
      if (error) {
        Sentry.captureException(error, {
          tags: { feature: "push_subscription_save_failed" },
        });
      }
      // Associate this subscription with the user's auth UUID so the
      // existing edge fns targeting include_aliases.external_id route to
      // this device. Defensive — App.tsx already calls registerUserForPush
      // on every onAuthStateChange, this just covers the case where the
      // user grants permission before the auth listener fires the login.
      if (granted) {
        try {
          assert(); await registerUserForPush(userId, guard?.isCurrent); assert();
        } catch (err) {
          assert();
          Sentry.captureException(err, {
            tags: { feature: "push_external_id_set_failed" },
          });
        }
      }
    },
    [userId],
  );

  const requestPermission = useCallback(async (guard?: PushPermissionOwnerGuard): Promise<PermissionOutcome> => {
    const assert = () => { if (guard && (!guard.isCurrent() || guard.ownerId !== userIdRef.current)) throw new Error("invalid_session"); };
    assert();
    if (guard) {
      const user = await supabase.auth.getUser(); assert();
      if (user.error || user.data.user?.id !== guard.ownerId) throw new Error("invalid_session");
      const session = await supabase.auth.getSession(); assert();
      if (session.error || session.data.session?.user.id !== guard.ownerId || !session.data.session.access_token) throw new Error("invalid_session");
    }
    if (!isSupported) return "unsupported";

    let outcome: PermissionOutcome;
    if (isNative()) {
      try {
        const granted = await requestNotificationPermission(guard?.isCurrent); assert();
        if (granted) {
          // Plugin v5: OS permission and subscription opt-in are SEPARATE
          // states — without the explicit optIn the dashboard stays on
          // "Never Subscribed / Never Prompted" even after iOS permission
          // was accepted (prod evidence, build 29 fresh install).
          await optInPushSubscription(guard?.isCurrent); assert();
        }
        setIsSubscribed(granted);
        outcome = granted ? "granted" : "denied";
      } catch (err) {
        assert();
        Sentry.captureException(err, {
          tags: { feature: "push_optin_request_failed_native" },
        });
        outcome = "denied";
      }
    } else {
      try {
        // Slidedown wraps the native browser permission prompt with a softer
        // pre-prompt UI. `force: true` shows it even if previously dismissed.
        assert(); await OneSignal.Slidedown.promptPush({ force: true }); assert();
        // The SDK doesn't return the outcome from promptPush; poll the
        // subscription state. Short delay lets the SDK settle after the
        // native prompt resolves.
        await new Promise((r) => setTimeout(r, 300)); assert();
        const sub = OneSignal.User?.PushSubscription;
        const optedIn = sub?.optedIn === true;
        const id = sub?.id ?? null;
        setIsSubscribed(optedIn);
        setSubscriptionId(id);
        outcome = optedIn ? "granted" : "denied";
      } catch (err) {
        assert();
        Sentry.captureException(err, {
          tags: { feature: "push_optin_request_failed" },
        });
        outcome = "denied";
      }
    }

    // Persist the opt-in decision so push_opt_in / push_opt_in_at stay in sync
    // no matter which UI path triggered the prompt (check-in sheet, Settings
    // toggle, Sleep). Reuses the existing writer; "unsupported" already returned
    // above, so only granted/denied reach here. saveOptInToDb itself no-ops when
    // there's no userId.
    assert(); await saveOptInToDb(outcome === "granted", guard); assert();
    return outcome;
  }, [isSupported, saveOptInToDb]);

  return { isSupported, isSubscribed, subscriptionId, requestPermission, saveOptInToDb };
}
